import React from 'react';
import { useDispatch } from 'react-redux';
import { LinearProgress, styled, Tooltip } from '@mui/material';
import { type GridColDef } from '@mui/x-data-grid-premium';

import { Icon, useSettings, Utils } from 'app/main/components';
import { ViewTable, ViewTableUtils } from 'app/main/components/page-layouts';
import type { ApiError } from 'app/main/components/Utils';

import SkillsUsersTableDetailsDialog from '../../components/SkillsUsersTableDetailsDialog';

const StyledViewTable = styled(ViewTable)(({ theme }) => ({
	'.MuiDataGrid-columnHeader': {
		'& .MuiDataGrid-columnHeaderTitleContainer, & .MuiDataGrid-columnHeaderTitleContainerContent': {
			paddingTop: '4px',
			paddingBottom: '4px'
		},
		'&:not(.SkillHeader) .MuiDataGrid-columnHeaderTitleContainer': {
			alignItems: 'flex-end'
		},
		'&.SkillHeader .MuiDataGrid-columnHeaderTitleContainer': {
			justifyContent: 'space-around',
			'& .MuiDataGrid-columnHeaderTitleContainerContent': {
				height: '100%',
				itemsAlign: 'end',
				'& .MuiDataGrid-columnHeaderTitle': {
					height: '100%',
					writingMode: 'sideways-lr',
					textOrientation: 'mixed',
					textWrap: 'pretty'
				}
			}
		}
	}
}));

export default function SkillsUsersTab() {
	const dispatch = useDispatch();
	const { t } = useSettings();

	const [columns, setColumns] = React.useState<GridColDef[]>([]);
	const [isLoading, setIsLoading] = React.useState(true);
	const [error, setError] = React.useState<ApiError>();

	const [refresh, setRefresh] = React.useState(true);
	const updateRefresh = () => setRefresh(value => !value);

	const [isDetailsDialogOpen, setIsDetailsDialogOpen] = React.useState(false);
	const [selectedUserSkill, setSelectedUserSkill] = React.useState<any>();

	const [defaultColumns] = React.useState<GridColDef[]>([
		{
			field: 'user_title',
			headerName: t('MODULE_USERS_NAME_SINGULAR'),
			minWidth: 300,
			flex: 1,
			renderCell: ({ value, row }) =>
				ViewTableUtils.renderCellWithTooltip({ value, to: { module: 'users', uid: row.user_uid } }),
			hideable: false,
			resizable: false
		}
	]);

	React.useEffect(() => {
		setIsLoading(true);
		Utils.executeStored({
			action: 'st_skills_list',
			dispatch,
			params: {
				n_page: 1,
				n_rows: 100
			},
			successHandler: response => {
				const tempColumns = [...defaultColumns];

				tempColumns.push(
					...(response.data.list?.map(skill => ({
						headerClassName: 'SkillHeader',
						field: skill.skll_uid,
						headerName: skill.skll_title,
						align: 'center',
						width: 60,
						valueGetter: (_value, row) =>
							row.user_skills.find(_userSkill => _userSkill.skll_uid === skill.skll_uid),
						valueFormatter: value => value.ussk_evaluations?.[0],
						renderCell: ({ formattedValue, value }) => {
							const userSkillLevel = formattedValue?.skev_level;

							if (Utils.parseDate(value?.skev_d_to)?.isSameOrBefore(Utils.today())) {
								return <Icon icon="warning" size={28} color="error.main" />;
							}

							if (userSkillLevel === 100) {
								return <Icon icon="check-circle" size={28} color="success.main" />;
							}

							if (userSkillLevel > 0) {
								const color = userSkillLevel > 50 ? 'success.main' : 'warning.main';
								const level = Math.floor((userSkillLevel * 8) / 100);

								return (
									<Tooltip
										title={ViewTableUtils.renderPercentage({ value: userSkillLevel, digits: 0 })}
										arrow
									>
										<Icon icon={`circle-slice-${level}`} size={28} color={color} />
									</Tooltip>
								);
							}

							return <Icon icon="circle-medium" color="text.disabled" />;
						},
						sortable: false,
						resizable: false
					})) || [])
				);

				setColumns(tempColumns);
				setIsLoading(false);
			},
			errorHandler: ex => {
				setError(ex);
				setIsLoading(false);
			}
		});
	}, [defaultColumns, dispatch, t]);

	if (isLoading) return <LinearProgress variant="indeterminate" />;

	return (
		<>
			<StyledViewTable
				module="users_skills"
				idField="user_uid"
				columns={columns}
				defaultFilters={[{ filt_field: 'user_enabled', filt_clause: 'equals', filt_value: 1 }]}
				refresh={refresh}
				updateRefresh={updateRefresh}
				showFilters={false}
				showExportButton={false}
				getCellClassName={({ field }) => {
					return field !== 'user_title' ? 'cursor-pointer' : '';
				}}
				handleCellClick={({ field, value }) => {
					if (field !== 'user_title') {
						setSelectedUserSkill(value);
						setIsDetailsDialogOpen(true);
					}
				}}
			/>
			<SkillsUsersTableDetailsDialog
				open={isDetailsDialogOpen}
				onClose={() => setIsDetailsDialogOpen(false)}
				model={selectedUserSkill}
				updateRefresh={updateRefresh}
			/>
		</>
	);
}
